import { useLocation } from '@tanstack/react-router';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';

import { getConfig } from '@app/config';
import { logger } from '@app/logger';

import { useAppStore } from '@common/stores/useAppStore';
import { getAuthHeader, isPreAuthPath } from '@common/utils/fetcher';

import { applyAuthResponse } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { isStorableUser, reportRefusedUser } from '@entities/auth/utils';

import { usePostAuth } from '@services/hooks/AuthService';
import {
  refreshLikesStore,
  refreshMatchesStore,
  refreshMessagesStore,
  refreshTasksStore,
} from '@services/StoreRefreshService';

import {
  type SSEHandler,
  type SSEMessage,
  type SSEMessageTypes,
} from './types';

export const useSse = (
  handlersRef: MutableRefObject<Partial<Record<SSEMessageTypes, SSEHandler[]>>>,
  initialHandlers: MutableRefObject<Set<() => void>>,
) => {
  const { pathname } = useLocation();
  const isEnabled = !isPreAuthPath(pathname);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);

  const { user } = useAuthStore();

  const { trigger: triggerPostAuth, data: dataPostAuth } = usePostAuth();

  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const currentUrlRef = useRef<string>('');
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    if (!dataPostAuth) {
      return;
    }

    // A response without a user is incomplete: keep everything the store has.
    if (!isStorableUser(dataPostAuth.user)) {
      reportRefusedUser('useSse', dataPostAuth.user);
      return;
    }

    // The account state refreshes with the auth store (plan §1).
    applyAuthResponse(dataPostAuth);
  }, [dataPostAuth]);

  useEffect(() => {
    getConfig().then(({ api_url }) => {
      setBaseUrl(api_url);
    });
  }, []);

  useEffect(() => {
    if (!baseUrl || !isEnabled) return;
    let es: EventSource | null = null;
    isUnmountedRef.current = false;

    const initData = () => {
      const store = useAppStore.getState();
      refreshLikesStore(store.setLikes);
      refreshMatchesStore(store);
      refreshTasksStore(store.setTasks);
    };

    const messageHandler = async (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as SSEMessage;
        const store = useAppStore.getState();

        Object.entries(handlersRef.current).forEach(([key, handlers]) => {
          if (key === data.type) {
            handlers?.forEach((handler) => handler(data));
          }
        });

        switch (data.type) {
          case 'tasks_updated':
            triggerPostAuth();
            refreshTasksStore(store.setTasks);
            break;
          case 'likes_received':
            refreshLikesStore(store.setLikes);
            break;
          case 'new_match':
            refreshMatchesStore(store, { limit: 10 });
            break;
          case 'new_chat_message': {
            const matchIdMatch =
              window.location.pathname.match(/\/chats\/([^/?#]+)/);
            const matchId = matchIdMatch?.[1];

            if (matchId) {
              refreshMessagesStore({ store, matchId });
            }

            refreshMatchesStore(store, { limit: 10 });
            break;
          }
          case 'profile_updated':
          case 'referral_stats_updated':
            triggerPostAuth();
            break;
        }
      } catch (err) {
        logger.captureException(err, {
          tags: { section: 'Bad SSE message' },
          extra: {
            url: currentUrlRef.current,
            event: e,
            err,
            data: e.data,
          },
        });
      }
    };

    const scheduleReconnect = () => {
      if (isUnmountedRef.current) return;
      const attempt = (reconnectAttemptRef.current += 1);
      const baseDelayMs = 1000;
      const maxDelayMs = 30000;
      const backoffMs = Math.min(
        maxDelayMs,
        baseDelayMs * Math.pow(2, attempt),
      );
      const jitterMs = Math.floor(Math.random() * 500);
      const delayMs = backoffMs + jitterMs;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      reconnectTimeoutRef.current = window.setTimeout(() => {
        if (!isEnabled || !baseUrl || isUnmountedRef.current) return;
        initEventSource();
      }, delayMs);
    };

    const initEventSource = () => {
      const authHeader = getAuthHeader();

      if (!authHeader) {
        return;
      }

      const authParam = encodeURIComponent(authHeader);
      const url = `${baseUrl}/sse?authorization=${authParam}`;
      currentUrlRef.current = url;
      es = new EventSource(url);

      es.onopen = () => {
        reconnectAttemptRef.current = 0;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        if (!user?.id) {
          triggerPostAuth();
        }
        initialHandlers.current?.forEach((handler) => handler());
      };

      es.onmessage = messageHandler;

      es.onerror = (e) => {
        if (e instanceof MessageEvent && e.data) {
          logger.captureException(e, {
            tags: { section: 'SSE error' },
            extra: { url, event: e },
          });
        }
        triggerPostAuth();
        es?.close();
        scheduleReconnect();
      };
    };

    initData();
    initEventSource();

    return () => {
      isUnmountedRef.current = true;
      es?.close();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [
    baseUrl,
    initialHandlers,
    isEnabled,
    triggerPostAuth,
    user?.id,
    handlersRef,
  ]);
};
